import { useState, useEffect } from 'react';
import api from '../api';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue
} from './ui/select';
import { Badge } from './ui/badge';
import { Bot, User } from 'lucide-react';

/**
 * AssigneeSelect - A reusable component for selecting assignees
 * Includes both human team members and AI agents
 */
export const AssigneeSelect = ({
  value,
  onValueChange,
  placeholder = "Select assignee",
  includeUnassigned = true,
  includeAI = true,
  filterByQueue = null, // Optional: filter AI agents by queue name
  className = ""
}) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [aiAgents, setAiAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignables();
  }, [includeAI]);

  const fetchAssignables = async () => {
    try {
      setLoading(true);
      const res = await api.get('/users/assignable', {
        params: { include_ai: includeAI }
      });
      setTeamMembers(res.data.team_members || []);
      
      let agents = res.data.ai_agents || [];
      // Filter by queue if specified
      if (filterByQueue && agents.length > 0) {
        agents = agents.filter(a => 
          a.primary_queues?.some(q => 
            q.toLowerCase().includes(filterByQueue.toLowerCase())
          )
        );
      }
      setAiAgents(agents);
    } catch (error) {
      console.error('Failed to fetch assignables:', error);
      // Fallback to basic team fetch
      try {
        const res = await api.get('/users/team');
        setTeamMembers((res.data || []).map(u => ({
          ...u,
          type: 'human',
          display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
        })));
      } catch (e) {
        console.error('Failed to fetch team:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDisplayValue = () => {
    if (!value || value === 'unassigned') return placeholder;
    
    // Check AI agents first
    const aiAgent = aiAgents.find(a => a.id === value);
    if (aiAgent) return aiAgent.display_name;
    
    // Check team members
    const member = teamMembers.find(m => m.id === value);
    if (member) return member.display_name;
    
    return placeholder;
  };

  return (
    <Select value={value || 'unassigned'} onValueChange={onValueChange}>
      <SelectTrigger className={className} data-testid="assignee-select">
        <SelectValue placeholder={placeholder}>
          {getDisplayValue()}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {includeUnassigned && (
          <SelectItem value="unassigned">
            <span className="text-muted-foreground">Unassigned</span>
          </SelectItem>
        )}
        
        {/* Human Team Members */}
        {teamMembers.length > 0 && (
          <SelectGroup>
            <SelectLabel className="flex items-center gap-2 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              Team Members
            </SelectLabel>
            {teamMembers.map(member => (
              <SelectItem key={member.id} value={member.id}>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </div>
                  <span>{member.display_name}</span>
                  {member.role && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                      {member.role}
                    </Badge>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        
        {/* AI Agents */}
        {includeAI && aiAgents.length > 0 && (
          <SelectGroup>
            <SelectLabel className="flex items-center gap-2 text-xs text-muted-foreground">
              <Bot className="h-3 w-3" />
              AI Agents
            </SelectLabel>
            {aiAgents.map(agent => (
              <SelectItem key={agent.id} value={agent.id}>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                    <Bot className="h-3 w-3 text-violet-700" />
                  </div>
                  <span>{agent.first_name}</span>
                  <Badge className="bg-violet-100 text-violet-700 text-[10px] px-1 py-0 h-4">
                    {agent.role_title}
                  </Badge>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        
        {!loading && teamMembers.length === 0 && aiAgents.length === 0 && (
          <div className="py-2 px-3 text-sm text-muted-foreground text-center">
            No assignees available
          </div>
        )}
      </SelectContent>
    </Select>
  );
};

/**
 * AssigneeBadge - Display the assigned person/AI with appropriate styling
 */
export const AssigneeBadge = ({ assigneeId, teamMembers = [], aiAgents = [], size = 'sm' }) => {
  // Check if it's an AI agent (prefixed with ai_)
  const isAI = assigneeId?.startsWith('ai_');
  
  if (isAI) {
    const agent = aiAgents.find(a => a.id === assigneeId);
    return (
      <div className="flex items-center gap-1">
        <div className={`${size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'} rounded-full bg-violet-100 flex items-center justify-center`}>
          <Bot className={`${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} text-violet-700`} />
        </div>
        <span className={`${size === 'sm' ? 'text-xs' : 'text-sm'} text-violet-700 font-medium`}>
          {agent?.first_name || 'AI Agent'}
        </span>
      </div>
    );
  }
  
  const member = teamMembers.find(m => m.id === assigneeId);
  if (!member) return null;
  
  return (
    <div className="flex items-center gap-1">
      <div className={`${size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'} rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-medium text-blue-700`}>
        {member.first_name?.[0]}{member.last_name?.[0]}
      </div>
      <span className={`${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
        {member.first_name} {member.last_name}
      </span>
    </div>
  );
};

export default AssigneeSelect;
